import { useEffect, useRef, useState, type ReactNode } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { Navigate, useParams } from 'react-router-dom';
import { useAssessmentByToken, useSaveProgress } from './hooks/useTakeAssessment';
import { isV2Payload } from './api/v2TakeApi';
import { PillarScreen } from './components/PillarScreen';
import { V2TakeFlow } from './components/V2TakeFlow';
import { CompletionScreen } from './components/CompletionScreen';
import {
  LandingScreen,
  VisionScreen,
  ConcernScreen,
  ExpiredNotice,
  NotFoundNotice,
  CompletionPlaceholder,
  CompletedReturnScreen,
} from './components/flowScreens';

type Step = 'landing' | 'vision' | number | 'concern' | 'completion';

/**
 * Anonymous client assessment flow (Feature 095, Slice 2).
 * Token-identified, no account. Answers save per pillar so progress survives.
 */
type TakeAssessmentPageProps = {
  /** Starts account creation at completion (route layer wires the signup modal). */
  onCreateAccount: (email: string) => void;
  /** Starts normal sign-in for clients whose email already has an account. */
  onSignIn: () => void;
};

export function TakeAssessmentPage({ onCreateAccount, onSignIn }: TakeAssessmentPageProps) {
  const { token = '' } = useParams();
  const { data, isLoading, isError } = useAssessmentByToken(token);
  // Separate mutation instances so a failed optional free-text save
  // (vision/concern) never leaks its error into the pillar answer-save UI, and
  // vice versa — each screen reads its own isPending/isError.
  const saveAnswers = useSaveProgress(token);
  const saveText = useSaveProgress(token);

  const [step, setStep] = useState<Step>('landing');
  const [answers, setAnswers] = useState<Record<string, number>>({});
  const seededToken = useRef<string | null>(null);
  const reduceMotion = useReducedMotion();

  // Seed saved answers when the assessment loads — keyed by token so switching to
  // a different link (React Router keeps the component instance) reseeds and
  // restarts the flow instead of keeping the previous assessment's answers/step.
  // An MVP 2 payload is skipped here: V2TakeFlow owns its own state and seeds
  // itself the same way, and its answers are responses rather than numbers.
  useEffect(() => {
    if (data && !isV2Payload(data) && seededToken.current !== token) {
      setAnswers(data.savedAnswers ?? {});
      setStep('landing');
      seededToken.current = token;
    }
  }, [data, token]);

  const shell = (children: ReactNode) => (
    <div className="min-h-screen bg-background">
      <header className="border-b border-gray-100 bg-[#0d5d4e] px-6 py-4">
        <span className="font-serif text-lg font-bold text-white">bQuest</span>
        <span className="ml-0.5 inline-block h-1.5 w-1.5 rounded-full bg-[#f3c66c] align-middle" />
      </header>
      <main className="px-4 py-10">{children}</main>
    </div>
  );

  // Fade + gentle rise between steps; direction-agnostic so back/forward both
  // read cleanly. Honors prefers-reduced-motion (fade only).
  const variants = reduceMotion
    ? { initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 } }
    : {
        initial: { opacity: 0, y: 12 },
        animate: { opacity: 1, y: 0 },
        exit: { opacity: 0, y: -12 },
      };

  const animated = (key: string, children: ReactNode) => (
    <AnimatePresence mode="wait" initial={false}>
      <motion.div
        key={key}
        variants={variants}
        initial="initial"
        animate="animate"
        exit="exit"
        transition={{ duration: 0.15, ease: 'easeOut' }}
      >
        {children}
      </motion.div>
    </AnimatePresence>
  );

  if (isLoading) return shell(<p className="text-center text-gray-500">Loading…</p>);
  if (isError)
    return shell(
      <p className="text-center text-gray-500">Unable to load assessment. Please try again.</p>,
    );
  if (!data) return shell(<NotFoundNotice />);
  if (data.isExpired) return shell(<ExpiredNotice />);
  // A finished test run goes straight to its report (Feature 118). The screen
  // below invites the holder to create an account to read theirs, which is the
  // right offer for a client and the wrong one for an advisor who already has an
  // account and is signed into it — it opened the guest signup modal. Fixing the
  // completion screens alone left this path, which is what a reopened or
  // refreshed token lands on.
  if (data.status === 'COMPLETED' && data.isSelfTest)
    return <Navigate to={`/longevity-assessment/results/${data.assessmentId}`} replace />;
  if (data.status === 'COMPLETED')
    return shell(
      <CompletedReturnScreen
        token={token}
        clientEmail={data.clientEmail}
        hasAccount={data.hasAccount}
        onCreateAccount={onCreateAccount}
        onSignIn={onSignIn}
      />,
    );

  // MVP 2 is a different flow, not a different set of questions in this one:
  // it opens with discovery and answers on a four-point scale. Which one the
  // client gets is decided by the payload, so a link sent before MVP 2 keeps
  // opening the V1 flow it promised.
  if (isV2Payload(data)) {
    return shell(
      <V2TakeFlow
        token={token}
        data={data}
        onCreateAccount={onCreateAccount}
        onSignIn={onSignIn}
        renderStep={animated}
      />,
    );
  }

  const pillars = data.content.pillars;

  const handleAnswer = (questionKey: string, value: 0 | 1 | 2) =>
    setAnswers((prev) => ({ ...prev, [questionKey]: value }));

  // Advance only after the pillar's answers persist — otherwise a failed save
  // (offline / 500) would let the client move on and later 400 at completion
  // with missing answers. On error we stay on the pillar (PillarScreen shows a
  // retry message).
  const savePillarAndAdvance = (index: number) => {
    const pillarAnswers = pillars[index].questions
      .filter((q) => answers[q.key] !== undefined)
      .map((q) => ({ questionKey: q.key, value: answers[q.key] as 0 | 1 | 2 }));
    saveAnswers.mutate(
      { answers: pillarAnswers },
      { onSuccess: () => setStep(index === pillars.length - 1 ? 'concern' : index + 1) },
    );
  };

  const renderStep = (): ReactNode => {
    if (step === 'landing') {
      return (
        <LandingScreen
          clientName={data.clientName}
          advisorName={data.advisorName}
          onStart={() => setStep('vision')}
        />
      );
    }
    if (step === 'vision') {
      return (
        <VisionScreen
          initialValue={data.savedVision ?? ''}
          onContinue={(value) => {
            if (value) saveText.mutate({ vision: value });
            setStep(0);
          }}
        />
      );
    }
    if (typeof step === 'number') {
      return (
        <PillarScreen
          pillar={pillars[step]}
          index={step}
          total={pillars.length}
          responseOptions={data.content.responseOptions}
          answers={answers}
          onAnswer={handleAnswer}
          onBack={() => setStep(step === 0 ? 'vision' : step - 1)}
          onContinue={() => savePillarAndAdvance(step)}
          isSaving={saveAnswers.isPending}
          saveError={saveAnswers.isError}
        />
      );
    }
    if (step === 'concern') {
      return (
        <ConcernScreen
          initialValue={data.savedConcern ?? ''}
          isSaving={saveText.isPending}
          onFinish={(value) => {
            saveText.mutate({ concern: value }, { onSuccess: () => setStep('completion') });
          }}
        />
      );
    }
    if (step === 'completion') {
      return (
        <CompletionScreen
          token={token}
          assessmentId={data.assessmentId}
          advisorName={data.advisorName}
          clientEmail={data.clientEmail}
          hasAccount={data.hasAccount}
          isSelfTest={data.isSelfTest}
          onCreateAccount={onCreateAccount}
          onSignIn={onSignIn}
        />
      );
    }
    return <CompletionPlaceholder />;
  };

  return shell(animated(String(step), renderStep()));
}
